import { useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { createSegmentation } from '../../../api/segmentations.js'
import {
  buildPatternsFromConditions,
  buildSegmentationNameFromConditions,
  createEmptyCondition,
  getUserParameterByKey,
  getUserParameterOptions,
} from './segmentationFormUtils.js'

const USER_PARAMETER_OPTIONS = getUserParameterOptions()

function isConditionComplete(condition) {
  const option = getUserParameterByKey(condition.key)
  if (!option) return false
  if (option.input === 'date_range') {
    const from = String(condition.from || '').trim()
    const to = String(condition.to || '').trim()
    return Boolean(from && to)
  }
  return Boolean(String(condition.value || '').trim())
}

function hasInvalidConditions(conditions) {
  return conditions.some((condition) => !isConditionComplete(condition))
}

function findDuplicateUniqueCondition(conditions) {
  const seen = new Set()
  for (const condition of conditions) {
    const option = getUserParameterByKey(condition.key)
    if (!option || option.allowMultiple) continue
    if (seen.has(option.key)) return option.label
    seen.add(option.key)
  }
  return null
}

function hasInvalidInstallDateRange(conditions) {
  for (const condition of conditions) {
    if (condition.key !== 'install_date') continue
    const from = String(condition.from || '').trim()
    const to = String(condition.to || '').trim()
    if (!from || !to) continue
    if (from > to) return true
  }
  return false
}

export default function SegmentationCreatePage() {
  const { applicationId } = useOutletContext()
  const navigate = useNavigate()

  const [groupConfigs, setGroupConfigs] = useState([])
  const [values, setValues] = useState({
    name: '',
    description: '',
    groupConfigId: '',
  })
  const [conditions, setConditions] = useState([createEmptyCondition()])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    listGroupConfigs(applicationId)
      .then((list) => {
        if (cancelled) return
        setGroupConfigs(list || [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [applicationId])

  function onChange(field) {
    return (e) =>
      setValues((prev) => ({
        ...prev,
        [field]: e.target.value,
      }))
  }

  function addCondition() {
    setConditions((prev) => [...prev, createEmptyCondition()])
  }

  function removeCondition(conditionId) {
    setConditions((prev) => prev.filter((item) => item.id !== conditionId))
  }

  function onConditionChange(conditionId, field) {
    return (e) => {
      const nextValue = e.target.value
      setConditions((prev) =>
        prev.map((item) => {
          if (item.id !== conditionId) return item
          if (field === 'key') {
            return {
              ...item,
              key: nextValue,
              value: '',
              from: '',
              to: '',
            }
          }
          return { ...item, [field]: nextValue }
        }),
      )
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (isSubmitting) return
    setError(null)

    if (conditions.length === 0) {
      setError(new Error('Add at least one user parameter.'))
      return
    }
    if (hasInvalidConditions(conditions)) {
      setError(new Error('Fill all user parameter values.'))
      return
    }
    const duplicateUniqueLabel = findDuplicateUniqueCondition(conditions)
    if (duplicateUniqueLabel) {
      setError(new Error(`You can only add one field "${duplicateUniqueLabel}".`))
      return
    }
    if (hasInvalidInstallDateRange(conditions)) {
      setError(new Error('Install date: "from" must be less than or equal to "to".'))
      return
    }
    const groupConfigId = String(values.groupConfigId || '').trim()
    if (!groupConfigId) {
      setError(new Error('Group config is required.'))
      return
    }

    setIsSubmitting(true)
    try {
      const patterns = buildPatternsFromConditions(conditions)
      if (patterns.length === 0) {
        throw new Error('Add at least one valid condition.')
      }
      const generatedName = buildSegmentationNameFromConditions(conditions)
      const name = String(values.name || '').trim() || generatedName
      if (!name) {
        throw new Error('Name is required.')
      }
      const payload = {
        name,
        description: values.description,
        patterns,
        groupConfigId: Number(groupConfigId),
      }
      const created = await createSegmentation(applicationId, payload)
      navigate(`/applications/${applicationId}/segments/${created.id}`)
    } catch (err) {
      setError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create segmentation</h1>
        <Link to={`/applications/${applicationId}/segments`} className="button buttonGhost">
          Back to segments
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Segmentation</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">Name</span>
            <input
              className="textInput"
              value={values.name}
              onChange={onChange('name')}
              placeholder="Optional, autogenerated from conditions"
            />
            <span className="muted mutedMeta">
              If empty, name is generated from selected user parameters.
            </span>
          </label>

          <label className="formField">
            <span className="formLabel">Description</span>
            <input
              className="textInput"
              value={values.description}
              onChange={onChange('description')}
              placeholder="Optional"
            />
          </label>

          <label className="formField">
            <span className="formLabel">Group config</span>
            <select
              className="textInput"
              value={values.groupConfigId}
              onChange={onChange('groupConfigId')}
              required
            >
              <option value="">—</option>
              {groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Config #${gc.id}`}
                </option>
              ))}
            </select>
          </label>

          <div className="formField">
            <span className="formLabel">User parameters</span>
            <div className="conditionsList">
              {conditions.map((condition) => {
                const option = getUserParameterByKey(condition.key)
                const canRemove = conditions.length > 1
                return (
                  <div key={condition.id} className="conditionItem">
                    <div className="conditionRow">
                      <select
                        className="textInput"
                        value={condition.key}
                        onChange={onConditionChange(condition.id, 'key')}
                      >
                        {USER_PARAMETER_OPTIONS.map((item) => (
                          <option key={item.key} value={item.key}>
                            {item.label}
                          </option>
                        ))}
                      </select>

                      {option?.input === 'select' ? (
                        <select
                          className="textInput"
                          value={condition.value}
                          onChange={onConditionChange(condition.id, 'value')}
                        >
                          <option value="">Select value</option>
                          {option.values.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      ) : option?.input === 'number' ? (
                        <input
                          className="textInput"
                          type="number"
                          value={condition.value}
                          onChange={onConditionChange(condition.id, 'value')}
                          placeholder="Value"
                        />
                      ) : option?.input === 'date_range' ? (
                        <div className="conditionDateRange">
                          <input
                            className="textInput"
                            type="date"
                            value={condition.from}
                            onChange={onConditionChange(condition.id, 'from')}
                          />
                          <input
                            className="textInput"
                            type="date"
                            value={condition.to}
                            onChange={onConditionChange(condition.id, 'to')}
                          />
                        </div>
                      ) : (
                        <input
                          className="textInput"
                          value={condition.value}
                          onChange={onConditionChange(condition.id, 'value')}
                          placeholder="Value"
                        />
                      )}

                      <button
                        type="button"
                        className="button buttonGhost"
                        disabled={!canRemove}
                        onClick={() => removeCondition(condition.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="sectionTop10">
              <button type="button" className="button buttonGhost" onClick={addCondition}>
                Add parameter
              </button>
            </div>
          </div>

          <div className="formActions">
            <button type="submit" className="button buttonPrimary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>

        {error ? <div className="formError">{String(error?.message || error)}</div> : null}
      </section>
    </div>
  )
}

